import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Divider,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Alert from '@material-ui/lab/Alert';
import { useNotify } from 'react-admin';
import axios from 'utils/axios';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';
import LaunchIcon from '@material-ui/icons/Launch';
import RefreshIcon from '@material-ui/icons/Refresh';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

// Constants
const DEBOUNCE_DELAY = 300;
const FBO_BALANCE_REFRESH_INTERVAL = 30000; // 30 seconds
const USER_TYPES = {
  SENDER: 'sender',
  RECIPIENT: 'recipient',
  FBO_USER: 'fbo_user',
};
const TRANSACTION_TYPES = {
  CREDIT: 'credit',
  REFUND: 'refund',
};
const API_ENDPOINTS = {
  USERS: '/v1/users',
  WALLETS: '/v1/wallets',
  WALLET_TRANSFER: '/v1/wallets/transfer',
  PLATFORM_BALANCE: '/v1/wallets/platform_balance',
  TRANSFER_TO_FBO: '/v1/wallets/transfer_to_fbo',
  FUND_FROM_FBO: '/v1/wallets/fund_from_fbo',
};

// Validation helpers
const DESCRIPTION_REGEX = /^[a-zA-Z0-9_-]+$/;
const DESCRIPTION_MAX_LENGTH = 100;

const validateDescription = description => {
  if (!description || description.trim() === '') {
    return 'Description is required';
  }
  if (description.length > DESCRIPTION_MAX_LENGTH) {
    return `Description must be ${DESCRIPTION_MAX_LENGTH} characters or less`;
  }
  if (!DESCRIPTION_REGEX.test(description)) {
    return 'Description can only contain letters, numbers, dashes, and underscores (no spaces)';
  }
  return null;
};

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      boxShadow: theme.shadows[6],
    },
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 2),
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  walletChip: {
    margin: theme.spacing(0.5),
    fontWeight: 500,
  },
  transferCard: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    background: 'linear-gradient(to right bottom, #ffffff, #f9faff)',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  },
  amountField: {
    '& input': {
      fontSize: '1.5rem',
      textAlign: 'center',
      fontWeight: 'bold',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.borderRadius * 2,
    },
  },
  transferButton: {
    borderRadius: theme.shape.borderRadius * 3,
    padding: theme.spacing(1, 4),
    fontWeight: 'bold',
    textTransform: 'none',
    fontSize: '1.1rem',
    boxShadow: theme.shadows[3],
  },
  profileButton: {
    borderRadius: theme.shape.borderRadius * 1.5,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
  },
  mainPaper: {
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  },
  pageTitle: {
    position: 'relative',
    marginBottom: theme.spacing(4),
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: -10,
      left: 0,
      width: 60,
      height: 4,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 2,
    },
  },
  fboBalanceCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: theme.spacing(2, 3),
    borderRadius: theme.shape.borderRadius * 2,
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fboBalanceAmount: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginLeft: 0,
  },
  tabPanel: {
    padding: theme.spacing(3, 0),
  },
  descriptionField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.borderRadius,
    },
  },
  successCard: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #a5d6a7',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  walletSelect: {
    minWidth: 300,
  },
}));

/**
 * Format transaction amount with color and sign
 */
const AmountField = ({ amount, kind }) => {
  const isPositive = [TRANSACTION_TYPES.CREDIT, TRANSACTION_TYPES.REFUND].includes(kind);
  const sign = isPositive ? '+' : '-';
  const color = isPositive ? 'green' : 'red';

  return <span style={{ color, fontWeight: 'bold' }}>{`${sign}$${amount}`}</span>;
};

/**
 * Recent transactions display component
 */
const TransactionList = React.memo(({ transactions }) => {
  if (!transactions?.length) return null;

  return (
    <Box mt={2}>
      <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold' }}>
        Recent Transactions
      </Typography>
      <Table size="small" style={{ background: '#f9f9f9', borderRadius: '4px' }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ fontWeight: 'bold' }}>Type</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Amount</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Loaded</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.slice(0, 3).map(tx => {
            const isPositiveTransaction = [
              TRANSACTION_TYPES.CREDIT,
              TRANSACTION_TYPES.REFUND,
            ].includes(tx.kind);

            return (
              <TableRow key={tx.id} hover>
                <TableCell>
                  <Chip
                    size="small"
                    label={tx.kind}
                    color={isPositiveTransaction ? 'primary' : 'default'}
                    variant={isPositiveTransaction ? 'default' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <AmountField amount={tx.amount} kind={tx.kind} />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={tx.loaded ? 'Loaded' : 'Pending'}
                    style={{
                      backgroundColor: tx.loaded ? '#e8f5e9' : '#fff3e0',
                      color: tx.loaded ? '#2e7d32' : '#e65100',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      height: 20,
                      borderRadius: 10,
                    }}
                    variant={tx.loaded ? 'default' : 'outlined'}
                  />
                </TableCell>
                <TableCell>{new Date(tx.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
});

/**
 * Wallet status chip component
 */
const StatusChip = ({ label, status, statusValue, className }) => (
  <Chip
    label={`${label}: ${statusValue || 'N/A'}`}
    color={status === 'verified' || status === 'completed' ? 'primary' : 'default'}
    size="small"
    className={className}
  />
);

/**
 * FBO Platform Balance display component
 */
const FBOBalanceDisplay = ({ balance, loading, onRefresh, classes }) => (
  <Box className={classes.fboBalanceCard}>
    <Box display="flex" alignItems="center">
      <AccountBalanceIcon style={{ fontSize: 40 }} />
      <Box ml={2}>
        <Typography variant="overline" style={{ opacity: 0.9 }}>
          FBO Platform Balance
        </Typography>
        {loading ? (
          <CircularProgress size={24} style={{ color: 'white' }} />
        ) : (
          <Typography className={classes.fboBalanceAmount}>
            $
            {balance !== null
              ? parseFloat(balance).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : '—'}
          </Typography>
        )}
      </Box>
    </Box>
    <Tooltip title="Refresh balance">
      <IconButton onClick={onRefresh} style={{ color: 'white' }} disabled={loading}>
        <RefreshIcon />
      </IconButton>
    </Tooltip>
  </Box>
);

/**
 * Tab panel wrapper component
 */
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`transfer-tabpanel-${index}`}
    aria-labelledby={`transfer-tab-${index}`}
    {...other}
  >
    {value === index && <Box pt={3}>{children}</Box>}
  </div>
);

/**
 * Confirmation dialog for FBO transfers
 */
const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  user,
  wallet,
  amount,
  description,
  direction,
  loading,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText component="div">
        <Box mb={2}>
          <Typography variant="body1" gutterBottom>
            Please confirm the following transfer:
          </Typography>
        </Box>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>User</TableCell>
              <TableCell>{user?.display_name || user?.first_name || user?.email}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>Wallet ID</TableCell>
              <TableCell>{wallet?.id}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell style={{ fontWeight: 'bold', color: '#3f51b5' }}>
                $
                {parseFloat(amount).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>Direction</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={direction}
                  color={direction === 'Wallet → FBO' ? 'secondary' : 'primary'}
                  icon={direction === 'Wallet → FBO' ? <ArrowForwardIcon /> : <ArrowBackIcon />}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell style={{ fontFamily: 'monospace' }}>{description}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        color="primary"
        variant="contained"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : null}
      >
        {loading ? 'Processing...' : 'Confirm Transfer'}
      </Button>
    </DialogActions>
  </Dialog>
);

/**
 * Success transaction display
 */
const TransactionSuccess = ({ transaction, onDismiss }) => {
  if (!transaction) return null;

  return (
    <Alert severity="success" onClose={onDismiss} style={{ marginTop: 16 }}>
      <Typography variant="subtitle2" gutterBottom>
        Transfer completed successfully!
      </Typography>
      <Box component="div" style={{ fontSize: '0.875rem' }}>
        <div>Transaction ID: {transaction.id}</div>
        <div>Amount: ${transaction.amount}</div>
        <div>Type: {transaction.kind}</div>
        {transaction.description && <div>Description: {transaction.description}</div>}
        <div>Date: {new Date(transaction.created_at).toLocaleString()}</div>
      </Box>
    </Alert>
  );
};

/**
 * Empty state card when no user is selected
 */
const EmptyUserCard = ({ type, classes }) => (
  <Card variant="outlined" className={classes.card}>
    <Box className={classes.cardHeader}>
      <Typography variant="h6">{type.charAt(0).toUpperCase() + type.slice(1)}</Typography>
    </Box>
    <CardContent className={classes.cardContent}>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={150}>
        <Typography variant="body1" color="textSecondary">
          Please select a {type} from the dropdown above
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

/**
 * User card with wallet information
 */
const UserCard = React.memo(({ user, wallet, label, transactions = [] }) => {
  const classes = useStyles();

  if (!user) return null;

  const displayName = user.display_name || user.first_name || user.email;

  return (
    <Card variant="outlined" className={classes.card} elevation={3}>
      <Box
        className={classes.cardHeader}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="h6">
          {label}: {displayName}
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Chip label={user.role || 'User'} size="small" color="secondary" />
          <Tooltip title="View User Profile">
            <IconButton
              size="small"
              component="a"
              href={`/users/${user.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'white' }}
            >
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </Box>

      <CardContent className={classes.cardContent} style={{ padding: '16px' }}>
        {/* User Info & Balance - Side by Side */}
        <Box display="flex" flexDirection="row" justifyContent="space-between" mb={2}>
          {/* User Info */}
          <Box flex="1">
            <Link
              component={RouterLink}
              to={`/users/${user.id}`}
              style={{ textDecoration: 'none' }}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#3f51b5' }}>
                {displayName} (ID: {user.id})
              </Typography>
            </Link>
            <Typography variant="body2" color="textSecondary">
              {user.email} {user.username && `• @${user.username}`}
            </Typography>
          </Box>

          {/* Wallet Summary */}
          {wallet && (
            <Box
              bgcolor="#f0f7ff"
              borderRadius={1}
              p={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              border="1px solid #e0e0ff"
              minWidth="150px"
            >
              <Typography variant="overline" display="block" style={{ fontWeight: 'bold' }}>
                BALANCE
              </Typography>
              <Typography variant="h5" style={{ color: '#3f51b5', fontWeight: 'bold' }}>
                ${wallet.true_balance}
              </Typography>
            </Box>
          )}
        </Box>

        {wallet && (
          <>
            {/* Status Badges in a Row */}
            <Box display="flex" flexWrap="wrap" mb={2} gap={1}>
              <StatusChip
                label="ID"
                status={wallet.identity_document_status}
                statusValue={wallet.identity_document_status}
                className={classes.walletChip}
              />
              <StatusChip
                label="Tax Form"
                status={wallet.tax_form_status}
                statusValue={wallet.tax_form_status}
                className={classes.walletChip}
              />
              {wallet.email && (
                <Chip
                  label={wallet.email}
                  size="small"
                  className={classes.walletChip}
                  variant="outlined"
                  icon={<span style={{ fontSize: '1.2em' }}>📧</span>}
                />
              )}
              <Chip
                label={`Wallet ID: ${wallet.id}`}
                size="small"
                variant="outlined"
                className={classes.walletChip}
              />
            </Box>

            {/* Recent Transactions Section */}
            {transactions.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <TransactionList transactions={transactions} />
              </>
            )}

            {/* View Profile Button */}
            <Divider style={{ margin: '16px 0' }} />
            <Box display="flex" justifyContent="center" mt={1}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<PersonIcon />}
                component="a"
                href={`/users/${user.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.profileButton}
                style={{ textTransform: 'none' }}
              >
                View User Profile
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
});

/**
 * User selection autocomplete component
 */
const UserSelector = ({ userType, value, users, onSelect, onSearch, loading, disabled }) => (
  <Autocomplete
    id={`${userType}-select`}
    options={users}
    getOptionLabel={user =>
      user?.id
        ? `${user?.display_name || user?.first_name || ''} ${user?.last_name ||
            ''} (${user?.email || user?.phone || ''}) ID: ${user?.id}`
        : ''
    }
    value={value}
    onChange={(event, newValue) => onSelect(userType, newValue)}
    onInputChange={(event, newInputValue) => {
      onSearch(userType, newInputValue);
    }}
    loading={loading}
    loadingText="Searching users..."
    noOptionsText="No users found"
    fullWidth
    openOnFocus={true}
    autoComplete={true}
    disableCloseOnSelect={false}
    filterOptions={options => options}
    renderInput={params => (
      <TextField
        {...params}
        label={`Select ${userType.charAt(0).toUpperCase() + userType.slice(1)}`}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    )}
    disabled={disabled}
  />
);

/**
 * Transfer amount form component
 */
const TransferForm = ({
  sender,
  recipient,
  senderWallet,
  recipientWallet,
  amount,
  setAmount,
  isTransferDisabled,
  transferring,
  handleTransfer,
  classes,
}) => (
  <Card className={classes.transferCard}>
    <CardContent>
      <Box textAlign="center">
        <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
          Transfer Amount
        </Typography>

        {/* Show transfer details */}
        {sender && recipient && senderWallet && recipientWallet && (
          <Box mb={3} p={1} bgcolor="#f0f7ff" borderRadius={1} maxWidth={500} mx="auto">
            <Typography variant="body2">
              Transfer from <strong>{sender.display_name || sender.first_name}</strong> to{' '}
              <strong>{recipient.display_name || recipient.first_name}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.9rem' }}>
              Sender available balance: ${senderWallet.available_balance}
            </Typography>
          </Box>
        )}

        <Box display="flex" flexDirection="column" alignItems="center" maxWidth={300} mx="auto">
          <TextField
            label="Amount to Transfer ($)"
            variant="outlined"
            type="number"
            className={classes.amountField}
            inputProps={{ min: 0, step: '0.01' }}
            value={amount}
            onChange={e => setAmount(e.target.value)}
            error={
              amount && senderWallet && parseFloat(amount) > parseFloat(senderWallet.true_balance)
            }
            helperText={
              amount && senderWallet && parseFloat(amount) > parseFloat(senderWallet.true_balance)
                ? 'Amount exceeds sender balance'
                : ''
            }
            disabled={transferring}
            fullWidth
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleTransfer}
            disabled={isTransferDisabled}
            size="large"
            className={classes.transferButton}
            style={{ marginTop: 20 }}
            startIcon={transferring ? <CircularProgress size={20} /> : null}
          >
            {transferring ? 'Processing...' : 'Transfer Funds'}
          </Button>

          {sender && recipient && sender.id === recipient.id && (
            <Typography variant="caption" color="error" style={{ marginTop: 8 }}>
              Sender and recipient cannot be the same
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/**
 * Main wallet transfers component
 */
const UserTransfersTab = ({ classes }) => {
  const notify = useNotify();

  // User state
  const [users, setUsers] = useState([]);
  const [sender, setSender] = useState(null);
  const [recipient, setRecipient] = useState(null);

  // Wallet state
  const [senderWallet, setSenderWallet] = useState(null);
  const [recipientWallet, setRecipientWallet] = useState(null);

  // Transaction state
  const [senderTransactions, setSenderTransactions] = useState([]);
  const [recipientTransactions, setRecipientTransactions] = useState([]);

  // Form state
  const [amount, setAmount] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Search state
  const [loading, setLoading] = useState({ sender: false, recipient: false });
  const [searchTimeout, setSearchTimeout] = useState({ sender: null, recipient: null });

  /**
   * Load wallet data for a user
   */
  const loadWalletData = useCallback(
    async (user, setWallet, setTransactions, userType) => {
      if (!user?.wallet_id) {
        setWallet(null);
        setTransactions([]);
        return;
      }

      try {
        const [walletResponse, transactionsResponse] = await Promise.all([
          axios.get(`${API_ENDPOINTS.WALLETS}/${user.wallet_id}`),
          axios.get(`${API_ENDPOINTS.WALLETS}/${user.wallet_id}/transactions?per_page=5`),
        ]);

        setWallet(walletResponse.data);

        if (transactionsResponse?.data) {
          setTransactions(transactionsResponse.data);
        }
      } catch (error) {
        notify(`Error loading ${userType} wallet: ${error.message}`, { type: 'error' });
        setWallet(null);
        setTransactions([]);
      }
    },
    [notify]
  );

  /**
   * Load sender wallet data when sender changes
   */
  useEffect(() => {
    loadWalletData(sender, setSenderWallet, setSenderTransactions, USER_TYPES.SENDER);
  }, [sender, loadWalletData]);

  /**
   * Load recipient wallet data when recipient changes
   */
  useEffect(() => {
    loadWalletData(recipient, setRecipientWallet, setRecipientTransactions, USER_TYPES.RECIPIENT);
  }, [recipient, loadWalletData]);

  /**
   * Refresh all data for a user
   */
  const refreshAllUserData = useCallback(
    async (user, setUser, setWallet, setTransactions, userType) => {
      if (!user?.id || !user?.wallet_id) {
        return null;
      }

      try {
        const [userResponse, walletResponse, transactionsResponse] = await Promise.all([
          axios.get(`${API_ENDPOINTS.USERS}/${user.id}`),
          axios.get(`${API_ENDPOINTS.WALLETS}/${user.wallet_id}`),
          axios.get(`${API_ENDPOINTS.WALLETS}/${user.wallet_id}/transactions?per_page=5`),
        ]);

        setUser(userResponse.data);
        setWallet(walletResponse.data);

        if (transactionsResponse?.data) {
          setTransactions(transactionsResponse.data);
        }

        return {
          user: userResponse.data,
          wallet: walletResponse.data,
          transactions: transactionsResponse.data,
        };
      } catch (error) {
        notify(`Error refreshing ${userType} data: ${error.message}`, { type: 'error' });
        return null;
      }
    },
    [notify]
  );
  /**
   * Handle wallet transfer with validation
   */
  const handleTransfer = useCallback(async () => {
    // Validate all required data exists
    if (!sender || !recipient || !senderWallet || !recipientWallet) {
      notify('Please select both sender and recipient', { type: 'warning' });
      return;
    }

    // Validate amount
    const transferAmount = parseFloat(amount);
    if (!amount || isNaN(transferAmount) || transferAmount <= 0) {
      notify('Please enter a valid amount greater than zero', { type: 'warning' });
      return;
    }

    // Validate sufficient balance
    if (transferAmount > parseFloat(senderWallet.true_balance)) {
      notify('Transfer amount exceeds sender balance', { type: 'warning' });
      return;
    }

    // Validate sender and recipient are different
    if (sender.id === recipient.id) {
      notify('Sender and recipient cannot be the same', { type: 'warning' });
      return;
    }

    // Proceed with transfer
    setTransferring(true);

    try {
      // Make transfer request
      await axios.post(API_ENDPOINTS.WALLET_TRANSFER, {
        transfer: {
          sender_id: sender.id,
          recipient_id: recipient.id,
          amount: transferAmount,
        },
      });

      notify('Transfer completed successfully', { type: 'success' });

      // Refresh all data after transfer
      await Promise.all([
        refreshAllUserData(
          sender,
          setSender,
          setSenderWallet,
          setSenderTransactions,
          USER_TYPES.SENDER
        ),
        refreshAllUserData(
          recipient,
          setRecipient,
          setRecipientWallet,
          setRecipientTransactions,
          USER_TYPES.RECIPIENT
        ),
      ]);

      setAmount(''); // Clear amount field
    } catch (error) {
      // Enhanced error handling
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || error.message;
      notify(`Error processing transfer: ${errorMessage}`, { type: 'error' });
    } finally {
      setTransferring(false);
    }
  }, [sender, recipient, senderWallet, recipientWallet, amount, notify, refreshAllUserData]);

  // Memoize the transfer disabled state for better performance
  const isTransferDisabled = useMemo(() => {
    // Base requirements check
    if (!sender || !recipient || !amount || !senderWallet || !recipientWallet || transferring) {
      return true;
    }

    const transferAmount = parseFloat(amount);

    // Amount validation
    if (isNaN(transferAmount) || transferAmount <= 0) {
      return true;
    }

    // Balance check
    if (transferAmount > parseFloat(senderWallet.true_balance)) {
      return true;
    }

    // Same user check
    if (sender.id === recipient.id) {
      return true;
    }

    return false;
  }, [sender, recipient, amount, senderWallet, recipientWallet, transferring]);

  /**
   * Load users on component mount
   */
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINTS.USERS}?per_page=30&completed=false`);
        if (response?.data) {
          setUsers(response.data);
        }
      } catch (error) {
        notify(`Error loading users: ${error.message}`, { type: 'error' });
      }
    };

    fetchAllUsers();
  }, [notify]);

  /**
   * Search users with debounce
   */
  const searchUsers = useCallback(
    (userType, query) => {
      // Clear any existing timeout
      if (searchTimeout[userType]) {
        clearTimeout(searchTimeout[userType]);
      }

      // Set new timeout
      const timeout = setTimeout(async () => {
        if (!query.trim()) {
          return;
        }

        setLoading(prev => ({ ...prev, [userType]: true }));

        try {
          // Create a proper URL with correctly encoded parameters
          const params = new URLSearchParams({
            page: '1',
            per_page: '50',
            completed: 'false',
            include_wallet: 'true',
            'filter[q]': query,
          });

          const response = await axios.get(`${API_ENDPOINTS.USERS}?${params.toString()}`);

          if (response?.data) {
            setUsers(response.data);
          }
        } catch (error) {
          notify(`Error searching users: ${error.message}`, { type: 'error' });
        } finally {
          setLoading(prev => ({ ...prev, [userType]: false }));
        }
      }, DEBOUNCE_DELAY);

      setSearchTimeout(prev => ({ ...prev, [userType]: timeout }));
    },
    [notify, searchTimeout]
  );

  /**
   * Handle user selection
   */
  const handleUserSelect = useCallback((userType, user) => {
    if (userType === USER_TYPES.SENDER) {
      setSender(user || null);
    } else {
      setRecipient(user || null);
    }
  }, []);

  return (
    <Box>
      <Grid container spacing={3}>
        {/* User Selection */}
        <Grid item xs={12} md={6}>
          <UserSelector
            userType={USER_TYPES.SENDER}
            value={sender}
            users={users}
            onSelect={handleUserSelect}
            onSearch={searchUsers}
            loading={loading[USER_TYPES.SENDER]}
            disabled={transferring}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <UserSelector
            userType={USER_TYPES.RECIPIENT}
            value={recipient}
            users={users}
            onSelect={handleUserSelect}
            onSearch={searchUsers}
            loading={loading[USER_TYPES.RECIPIENT]}
            disabled={transferring}
          />
        </Grid>

        {/* User Cards */}
        <Grid item xs={12} md={6}>
          {sender ? (
            <UserCard
              user={sender}
              wallet={senderWallet}
              label="Sender"
              transactions={senderTransactions}
            />
          ) : (
            <EmptyUserCard type={USER_TYPES.SENDER} classes={classes} />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {recipient ? (
            <UserCard
              user={recipient}
              wallet={recipientWallet}
              label="Recipient"
              transactions={recipientTransactions}
            />
          ) : (
            <EmptyUserCard type={USER_TYPES.RECIPIENT} classes={classes} />
          )}
        </Grid>

        {/* Transfer Section */}
        <Grid item xs={12}>
          <TransferForm
            sender={sender}
            recipient={recipient}
            senderWallet={senderWallet}
            recipientWallet={recipientWallet}
            amount={amount}
            setAmount={setAmount}
            isTransferDisabled={isTransferDisabled}
            transferring={transferring}
            handleTransfer={handleTransfer}
            classes={classes}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

/**
 * Transfer to FBO Tab - Move funds from user wallet to FBO balance
 */
const TransferToFBOTab = ({ classes, fboBalance, fboLoading, refreshFBOBalance }) => {
  const notify = useNotify();

  // User state
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userWallets, setUserWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);

  // Form state
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [successTransaction, setSuccessTransaction] = useState(null);

  // Dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Search state
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Validation
  const descriptionError = description ? validateDescription(description) : null;

  /**
   * Load user's wallets when user is selected
   */
  useEffect(() => {
    const loadUserWallets = async () => {
      if (!selectedUser?.id) {
        setUserWallets([]);
        setSelectedWallet(null);
        return;
      }

      try {
        // If user has wallet_id, fetch that wallet
        if (selectedUser.wallet_id) {
          const response = await axios.get(`${API_ENDPOINTS.WALLETS}/${selectedUser.wallet_id}`);
          if (response?.data) {
            setUserWallets([response.data]);
            setSelectedWallet(response.data);
          }
        } else {
          setUserWallets([]);
          setSelectedWallet(null);
        }
      } catch (error) {
        notify(`Error loading user wallets: ${error.message}`, { type: 'error' });
        setUserWallets([]);
        setSelectedWallet(null);
      }
    };

    loadUserWallets();
  }, [selectedUser, notify]);

  /**
   * Load initial users
   */
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINTS.USERS}?per_page=30&completed=false`);
        if (response?.data) {
          setUsers(response.data);
        }
      } catch (error) {
        notify(`Error loading users: ${error.message}`, { type: 'error' });
      }
    };

    fetchAllUsers();
  }, [notify]);

  /**
   * Search users with debounce
   */
  const searchUsers = useCallback(
    query => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      const timeout = setTimeout(async () => {
        if (!query.trim()) return;

        setLoading(true);

        try {
          const params = new URLSearchParams({
            page: '1',
            per_page: '50',
            completed: 'false',
            include_wallet: 'true',
            'filter[q]': query,
          });

          const response = await axios.get(`${API_ENDPOINTS.USERS}?${params.toString()}`);
          if (response?.data) {
            setUsers(response.data);
          }
        } catch (error) {
          notify(`Error searching users: ${error.message}`, { type: 'error' });
        } finally {
          setLoading(false);
        }
      }, DEBOUNCE_DELAY);

      setSearchTimeout(timeout);
    },
    [notify, searchTimeout]
  );

  /**
   * Handle transfer submission
   */
  const handleTransfer = useCallback(async () => {
    setTransferring(true);
    setConfirmOpen(false);

    try {
      const response = await axios.post(API_ENDPOINTS.TRANSFER_TO_FBO, {
        fbo_transfer: {
          wallet_id: selectedWallet.id,
          amount: parseFloat(amount),
          description: description,
        },
      });

      notify('Transfer to FBO completed successfully', { type: 'success' });
      setSuccessTransaction(response.data?.transaction || response.data);

      // Refresh wallet data
      if (selectedUser?.wallet_id) {
        const walletResponse = await axios.get(
          `${API_ENDPOINTS.WALLETS}/${selectedUser.wallet_id}`
        );
        if (walletResponse?.data) {
          setUserWallets([walletResponse.data]);
          setSelectedWallet(walletResponse.data);
        }
      }

      // Refresh FBO balance
      refreshFBOBalance();

      // Clear form
      setAmount('');
      setDescription('');
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || error.message;
      notify(`Error processing transfer: ${errorMessage}`, { type: 'error' });
    } finally {
      setTransferring(false);
    }
  }, [selectedWallet, amount, description, selectedUser, notify, refreshFBOBalance]);

  // Check if transfer is valid
  const isTransferDisabled = useMemo(() => {
    if (!selectedUser || !selectedWallet || !amount || transferring) return true;
    const transferAmount = parseFloat(amount);
    if (isNaN(transferAmount) || transferAmount <= 0) return true;
    if (transferAmount > parseFloat(selectedWallet.true_balance)) return true;
    if (!description || validateDescription(description)) return true;
    return false;
  }, [selectedUser, selectedWallet, amount, description, transferring]);

  return (
    <Box>
      <FBOBalanceDisplay
        balance={fboBalance}
        loading={fboLoading}
        onRefresh={refreshFBOBalance}
        classes={classes}
      />

      <Grid container spacing={3}>
        {/* User Selection */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            id="fbo-user-select"
            options={users}
            getOptionLabel={user =>
              user?.id
                ? `${user?.display_name || user?.first_name || ''} ${user?.last_name ||
                    ''} (${user?.email || user?.phone || ''}) ID: ${user?.id}`
                : ''
            }
            value={selectedUser}
            onChange={(event, newValue) => {
              setSelectedUser(newValue);
              setSuccessTransaction(null);
            }}
            onInputChange={(event, newInputValue) => searchUsers(newInputValue)}
            loading={loading}
            loadingText="Searching users..."
            noOptionsText="No users found"
            fullWidth
            filterOptions={options => options}
            renderInput={params => (
              <TextField
                {...params}
                label="Select User"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={transferring}
          />
        </Grid>

        {/* Wallet Selection */}
        <Grid item xs={12} md={6}>
          {userWallets.length > 0 ? (
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Select Wallet</InputLabel>
              <Select
                value={selectedWallet?.id || ''}
                onChange={e => {
                  const wallet = userWallets.find(w => w.id === e.target.value);
                  setSelectedWallet(wallet);
                }}
                label="Select Wallet"
                disabled={transferring}
              >
                {userWallets.map(wallet => (
                  <MenuItem key={wallet.id} value={wallet.id}>
                    Wallet #{wallet.id} - Balance: ${wallet.true_balance}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Select Wallet"
              variant="outlined"
              fullWidth
              disabled
              value={selectedUser ? 'No wallets available' : 'Select a user first'}
            />
          )}
        </Grid>

        {/* User Card */}
        <Grid item xs={12} md={6}>
          {selectedUser ? (
            <UserCard
              user={selectedUser}
              wallet={selectedWallet}
              label="Selected User"
              transactions={[]}
            />
          ) : (
            <EmptyUserCard type="user" classes={classes} />
          )}
        </Grid>

        {/* Empty placeholder for layout consistency */}
        <Grid item xs={12} md={6} />

        {/* Transfer Form */}
        <Grid item xs={12}>
          <Card className={classes.transferCard}>
            <CardContent>
              <Box textAlign="center">
                <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
                  <ArrowForwardIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  Transfer to FBO
                </Typography>

                {selectedWallet && (
                  <Box mb={3} p={2} bgcolor="#fff3e0" borderRadius={1} maxWidth={500} mx="auto">
                    <Typography variant="body2">
                      Transfer from{' '}
                      <strong>{selectedUser?.display_name || selectedUser?.first_name}</strong>'s
                      wallet to FBO
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ fontSize: '0.9rem' }}
                    >
                      Available balance: ${selectedWallet.true_balance}
                    </Typography>
                  </Box>
                )}

                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  maxWidth={400}
                  mx="auto"
                >
                  <TextField
                    label="Amount to Transfer ($)"
                    variant="outlined"
                    type="number"
                    className={classes.amountField}
                    inputProps={{ min: 0, step: '0.01' }}
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    error={
                      amount &&
                      selectedWallet &&
                      parseFloat(amount) > parseFloat(selectedWallet.true_balance)
                    }
                    helperText={
                      amount &&
                      selectedWallet &&
                      parseFloat(amount) > parseFloat(selectedWallet.true_balance)
                        ? 'Amount exceeds wallet balance'
                        : ''
                    }
                    disabled={transferring}
                    fullWidth
                    style={{ marginBottom: 16 }}
                  />

                  <TextField
                    label="Description"
                    variant="outlined"
                    className={classes.descriptionField}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    error={!!descriptionError}
                    helperText={
                      descriptionError || 'Use dashed format (e.g., refund-user-123-jan-2026)'
                    }
                    disabled={transferring}
                    fullWidth
                    inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                    style={{ marginBottom: 16 }}
                  />

                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setConfirmOpen(true)}
                    disabled={isTransferDisabled}
                    size="large"
                    className={classes.transferButton}
                    startIcon={transferring ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
                  >
                    {transferring ? 'Processing...' : 'Transfer to FBO'}
                  </Button>
                </Box>

                {successTransaction && (
                  <TransactionSuccess
                    transaction={successTransaction}
                    onDismiss={() => setSuccessTransaction(null)}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleTransfer}
        title="Confirm Transfer to FBO"
        user={selectedUser}
        wallet={selectedWallet}
        amount={amount}
        description={description}
        direction="Wallet → FBO"
        loading={transferring}
      />
    </Box>
  );
};

/**
 * Fund from FBO Tab - Move funds from FBO balance to user wallet
 */
const FundFromFBOTab = ({ classes, fboBalance, fboLoading, refreshFBOBalance }) => {
  const notify = useNotify();

  // User state
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userWallets, setUserWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);

  // Form state
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [successTransaction, setSuccessTransaction] = useState(null);

  // Dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Search state
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Validation
  const descriptionError = description ? validateDescription(description) : null;

  /**
   * Load user's wallets when user is selected
   */
  useEffect(() => {
    const loadUserWallets = async () => {
      if (!selectedUser?.id) {
        setUserWallets([]);
        setSelectedWallet(null);
        return;
      }

      try {
        if (selectedUser.wallet_id) {
          const response = await axios.get(`${API_ENDPOINTS.WALLETS}/${selectedUser.wallet_id}`);
          if (response?.data) {
            setUserWallets([response.data]);
            setSelectedWallet(response.data);
          }
        } else {
          setUserWallets([]);
          setSelectedWallet(null);
        }
      } catch (error) {
        notify(`Error loading user wallets: ${error.message}`, { type: 'error' });
        setUserWallets([]);
        setSelectedWallet(null);
      }
    };

    loadUserWallets();
  }, [selectedUser, notify]);

  /**
   * Load initial users
   */
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINTS.USERS}?per_page=30&completed=false`);
        if (response?.data) {
          setUsers(response.data);
        }
      } catch (error) {
        notify(`Error loading users: ${error.message}`, { type: 'error' });
      }
    };

    fetchAllUsers();
  }, [notify]);

  /**
   * Search users with debounce
   */
  const searchUsers = useCallback(
    query => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      const timeout = setTimeout(async () => {
        if (!query.trim()) return;

        setLoading(true);

        try {
          const params = new URLSearchParams({
            page: '1',
            per_page: '50',
            completed: 'false',
            include_wallet: 'true',
            'filter[q]': query,
          });

          const response = await axios.get(`${API_ENDPOINTS.USERS}?${params.toString()}`);
          if (response?.data) {
            setUsers(response.data);
          }
        } catch (error) {
          notify(`Error searching users: ${error.message}`, { type: 'error' });
        } finally {
          setLoading(false);
        }
      }, DEBOUNCE_DELAY);

      setSearchTimeout(timeout);
    },
    [notify, searchTimeout]
  );

  /**
   * Handle transfer submission
   */
  const handleTransfer = useCallback(async () => {
    setTransferring(true);
    setConfirmOpen(false);

    try {
      const response = await axios.post(API_ENDPOINTS.FUND_FROM_FBO, {
        fbo_transfer: {
          wallet_id: selectedWallet.id,
          amount: parseFloat(amount),
          description: description,
        },
      });

      notify('Fund from FBO completed successfully', { type: 'success' });
      setSuccessTransaction(response.data?.transaction || response.data);

      // Refresh wallet data
      if (selectedUser?.wallet_id) {
        const walletResponse = await axios.get(
          `${API_ENDPOINTS.WALLETS}/${selectedUser.wallet_id}`
        );
        if (walletResponse?.data) {
          setUserWallets([walletResponse.data]);
          setSelectedWallet(walletResponse.data);
        }
      }

      // Refresh FBO balance
      refreshFBOBalance();

      // Clear form
      setAmount('');
      setDescription('');
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || error.message;
      notify(`Error processing transfer: ${errorMessage}`, { type: 'error' });
    } finally {
      setTransferring(false);
    }
  }, [selectedWallet, amount, description, selectedUser, notify, refreshFBOBalance]);

  // Check if transfer is valid
  const isTransferDisabled = useMemo(() => {
    if (!selectedUser || !selectedWallet || !amount || transferring) return true;
    const transferAmount = parseFloat(amount);
    if (isNaN(transferAmount) || transferAmount <= 0) return true;
    if (fboBalance !== null && transferAmount > parseFloat(fboBalance)) return true;
    if (!description || validateDescription(description)) return true;
    return false;
  }, [selectedUser, selectedWallet, amount, description, transferring, fboBalance]);

  return (
    <Box>
      <FBOBalanceDisplay
        balance={fboBalance}
        loading={fboLoading}
        onRefresh={refreshFBOBalance}
        classes={classes}
      />

      <Grid container spacing={3}>
        {/* User Selection */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            id="fbo-fund-user-select"
            options={users}
            getOptionLabel={user =>
              user?.id
                ? `${user?.display_name || user?.first_name || ''} ${user?.last_name ||
                    ''} (${user?.email || user?.phone || ''}) ID: ${user?.id}`
                : ''
            }
            value={selectedUser}
            onChange={(event, newValue) => {
              setSelectedUser(newValue);
              setSuccessTransaction(null);
            }}
            onInputChange={(event, newInputValue) => searchUsers(newInputValue)}
            loading={loading}
            loadingText="Searching users..."
            noOptionsText="No users found"
            fullWidth
            filterOptions={options => options}
            renderInput={params => (
              <TextField
                {...params}
                label="Select User"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={transferring}
          />
        </Grid>

        {/* Wallet Selection */}
        <Grid item xs={12} md={6}>
          {userWallets.length > 0 ? (
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Select Wallet</InputLabel>
              <Select
                value={selectedWallet?.id || ''}
                onChange={e => {
                  const wallet = userWallets.find(w => w.id === e.target.value);
                  setSelectedWallet(wallet);
                }}
                label="Select Wallet"
                disabled={transferring}
              >
                {userWallets.map(wallet => (
                  <MenuItem key={wallet.id} value={wallet.id}>
                    Wallet #{wallet.id} - Balance: ${wallet.true_balance}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Select Wallet"
              variant="outlined"
              fullWidth
              disabled
              value={selectedUser ? 'No wallets available' : 'Select a user first'}
            />
          )}
        </Grid>

        {/* User Card */}
        <Grid item xs={12} md={6}>
          {selectedUser ? (
            <UserCard
              user={selectedUser}
              wallet={selectedWallet}
              label="Selected User"
              transactions={[]}
            />
          ) : (
            <EmptyUserCard type="user" classes={classes} />
          )}
        </Grid>

        {/* Empty placeholder for layout consistency */}
        <Grid item xs={12} md={6} />

        {/* Transfer Form */}
        <Grid item xs={12}>
          <Card className={classes.transferCard}>
            <CardContent>
              <Box textAlign="center">
                <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
                  <ArrowBackIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  Fund from FBO
                </Typography>

                {selectedWallet && (
                  <Box mb={3} p={2} bgcolor="#e3f2fd" borderRadius={1} maxWidth={500} mx="auto">
                    <Typography variant="body2">
                      Fund <strong>{selectedUser?.display_name || selectedUser?.first_name}</strong>
                      's wallet from FBO
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ fontSize: '0.9rem' }}
                    >
                      FBO available: $
                      {fboBalance !== null
                        ? parseFloat(fboBalance).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : '—'}
                    </Typography>
                  </Box>
                )}

                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  maxWidth={400}
                  mx="auto"
                >
                  <TextField
                    label="Amount to Transfer ($)"
                    variant="outlined"
                    type="number"
                    className={classes.amountField}
                    inputProps={{ min: 0, step: '0.01' }}
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    error={
                      amount && fboBalance !== null && parseFloat(amount) > parseFloat(fboBalance)
                    }
                    helperText={
                      amount && fboBalance !== null && parseFloat(amount) > parseFloat(fboBalance)
                        ? 'Amount exceeds FBO balance'
                        : ''
                    }
                    disabled={transferring}
                    fullWidth
                    style={{ marginBottom: 16 }}
                  />

                  <TextField
                    label="Description"
                    variant="outlined"
                    className={classes.descriptionField}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    error={!!descriptionError}
                    helperText={
                      descriptionError || 'Use dashed format (e.g., load-user-123-jan-2026)'
                    }
                    disabled={transferring}
                    fullWidth
                    inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                    style={{ marginBottom: 16 }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setConfirmOpen(true)}
                    disabled={isTransferDisabled}
                    size="large"
                    className={classes.transferButton}
                    startIcon={transferring ? <CircularProgress size={20} /> : <ArrowBackIcon />}
                  >
                    {transferring ? 'Processing...' : 'Fund from FBO'}
                  </Button>
                </Box>

                {successTransaction && (
                  <TransactionSuccess
                    transaction={successTransaction}
                    onDismiss={() => setSuccessTransaction(null)}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleTransfer}
        title="Confirm Fund from FBO"
        user={selectedUser}
        wallet={selectedWallet}
        amount={amount}
        description={description}
        direction="FBO → Wallet"
        loading={transferring}
      />
    </Box>
  );
};

/**
 * Main Transfers component with tabs
 */
const WalletTransfers = () => {
  const classes = useStyles();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // FBO Balance state
  const [fboBalance, setFboBalance] = useState(null);
  const [fboLoading, setFboLoading] = useState(false);
  const fboRefreshInterval = useRef(null);

  /**
   * Fetch FBO platform balance
   */
  const fetchFBOBalance = useCallback(async () => {
    setFboLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.PLATFORM_BALANCE);
      setFboBalance(response.data?.balance ?? response.data?.platform_balance ?? null);
    } catch (error) {
      console.error('Error fetching FBO balance:', error);
      // Don't show error notification for auto-refresh failures
    } finally {
      setFboLoading(false);
    }
  }, []);

  /**
   * Setup FBO balance auto-refresh
   */
  useEffect(() => {
    if (activeTab === 1 || activeTab === 2) {
      fetchFBOBalance();

      // Set up auto-refresh interval
      fboRefreshInterval.current = setInterval(fetchFBOBalance, FBO_BALANCE_REFRESH_INTERVAL);

      return () => {
        if (fboRefreshInterval.current) {
          clearInterval(fboRefreshInterval.current);
        }
      };
    }
  }, [activeTab, fetchFBOBalance]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Paper className={classes.mainPaper} style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" className={classes.pageTitle}>
        Transfers
      </Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        style={{ marginBottom: 16 }}
      >
        <Tab label="User Transfers" />
        <Tab label="Wallet → FBO" />
        <Tab label="FBO → Wallet" />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <UserTransfersTab classes={classes} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <TransferToFBOTab
          classes={classes}
          fboBalance={fboBalance}
          fboLoading={fboLoading}
          refreshFBOBalance={fetchFBOBalance}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <FundFromFBOTab
          classes={classes}
          fboBalance={fboBalance}
          fboLoading={fboLoading}
          refreshFBOBalance={fetchFBOBalance}
        />
      </TabPanel>
    </Paper>
  );
};

export default WalletTransfers;
